import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Field, inputCls } from "@/components/AuthShell";
import { LEVELS } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";

const empty = { name: "", username: "", email: "", phone: "", school: "", country: "", password: "", level: 1, role: "user", is_active: true, subscription_until: "", levels: [] };

export const UserDialog = ({ open, onOpenChange, initial, onSubmit, saving }) => {
  const [form, setForm] = useState(empty);
  const editing = Boolean(initial?.id);

  useEffect(() => {
    if (open) setForm(initial ? { ...empty, ...initial, password: "", subscription_until: (initial.subscription_until || "").slice(0, 10) } : empty);
  }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    const payload = { name: form.name, email: form.email.trim(), phone: form.phone || "", school: form.school || "", country: form.country || "", level: Number(form.level), role: form.role, is_active: form.is_active };
    if (form.username.trim()) payload.username = form.username.trim().toLowerCase();
    if (!payload.email && !editing) delete payload.email;
    if (form.password) payload.password = form.password;
    if (form.role === "user" && form.subscription_until) payload.subscription_until = form.subscription_until;
    if (form.role === "teacher") payload.levels = form.levels || [];
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FAF7F2] max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="admin-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-right">{editing ? "تعديل بيانات المستخدم" : "إضافة حساب جديد"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-user-form">
          <Field label="الاسم"><input required minLength={2} className={inputCls} value={form.name} onChange={set("name")} data-testid="admin-form-name" /></Field>
          {form.role !== "admin" && (
            <Field label="اسم الدخول (حروف إنجليزية وأرقام، يُستخدم للدخول)">
              <input required pattern="[A-Za-z0-9._\-]{3,30}" dir="ltr" autoCapitalize="off" className={inputCls} value={form.username || ""}
                onChange={(e) => setForm({ ...form, username: e.target.value.replace(/\s/g, "") })} data-testid="admin-form-username" />
            </Field>
          )}
          <Field label={form.role === "admin" ? "البريد الإلكتروني" : "البريد الإلكتروني (اختياري)"}><input type="email" required={form.role === "admin"} dir="ltr" className={inputCls} value={form.email || ""} onChange={set("email")} data-testid="admin-form-email" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="الهاتف"><input dir="ltr" className={inputCls} value={form.phone} onChange={set("phone")} data-testid="admin-form-phone" /></Field>
            <Field label="المستوى">
              <select className={inputCls} value={form.level} onChange={set("level")} data-testid="admin-form-level">
                {LEVELS.map((l) => <option key={l.n} value={l.n}>{l.n} · {l.name}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="اسم المدرسة"><input maxLength={150} className={inputCls} value={form.school} onChange={set("school")} data-testid="admin-form-school" /></Field>
            <Field label="الدولة">
              <select className={inputCls} value={form.country} onChange={set("country")} data-testid="admin-form-country">
                <option value="">بدون دولة</option>
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="الدور">
              <select className={inputCls} value={form.role} onChange={set("role")} data-testid="admin-form-role">
                <option value="user">طالب / قارئ</option>
                <option value="teacher">معلم</option>
                <option value="admin">مدير</option>
              </select>
            </Field>
            <Field label={editing ? "كلمة مرور جديدة (اختياري)" : "كلمة المرور"}>
              <input type="password" dir="ltr" required={!editing} minLength={6} className={inputCls} value={form.password} onChange={set("password")} data-testid="admin-form-password" />
            </Field>
          </div>
          <label className="flex items-center justify-between bg-white border border-[#E5DCCB] p-4">
            <span className="font-semibold text-sm">الحساب مفعّل</span>
            <Switch dir="ltr" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="admin-form-active" />
          </label>
          {form.role === "teacher" && (
            <Field label="مستويات المعلم (يرى طلاب وكتب هذه المستويات فقط، وبدون تحديد يرى الكل)">
              <div className="flex flex-wrap gap-2" data-testid="admin-form-levels">
                {LEVELS.map((l) => {
                  const on = (form.levels || []).includes(l.n);
                  return (
                    <button type="button" key={l.n} data-testid={`admin-form-level-${l.n}`} aria-pressed={on}
                      onClick={() => setForm((f) => ({ ...f, levels: on ? f.levels.filter((x) => x !== l.n) : [...(f.levels || []), l.n].sort() }))}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-[#047857] text-white border-[#047857]" : "border-[#E5DCCB] bg-white hover:border-[#047857]"}`}>
                      {l.n} · {l.name}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}
          {form.role === "user" && (
            <Field label="الاشتراك سارٍ حتى (اتركه فارغًا لسنة من اليوم عند التفعيل)">
              <input type="date" dir="ltr" className={inputCls} value={form.subscription_until} onChange={set("subscription_until")} data-testid="admin-form-subscription" />
            </Field>
          )}
          <button disabled={saving} className="w-full h-12 rounded-full bg-[#1C1917] text-[#FAF7F2] font-semibold hover:bg-[#8B3A2B] transition-colors disabled:opacity-60" data-testid="admin-create-user-submit-btn">
            {saving ? "جارٍ الحفظ..." : editing ? "حفظ التعديلات" : "إنشاء الحساب"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
